/**
 * MessageBubble - Individual message within a thread.
 *
 * Renders a single inbound or outbound message in the ConversationView.
 * Inbound messages are left-aligned; outbound are right-aligned.
 * Click on raw headers section opens JsonInspector for full headers.
 */

import { useState, type CSSProperties, type ReactNode } from 'react'
import { Paperclip, ChevronDown, ChevronUp } from 'lucide-react'
import { ChatMarkdown } from '@/shared/components/ChatMarkdown'
import { JsonInspector } from '@/shared/components/JsonInspector'
import type { Message } from '../types'
import { formatUnixSeconds } from '../dateUtils'

// ── Props ─────────────────────────────────────────────────────────────────────

interface MessageBubbleProps {
  message: Message
}

// ── Component ────────────────────────────────────────────────────────────────

export function MessageBubble({ message }: MessageBubbleProps): ReactNode {
  const [showHeaders, setShowHeaders] = useState(false)
  const isOutbound = message.direction === 'outbound'

  // The thread endpoint (GET /v1/interactions/{id}/thread) serializes the
  // plain-text body under the `body_text` key, while other surfaces use
  // `body`. Fall back so the bubble renders text regardless of which field
  // the API populated — mirrors MessageView / CompleteHistoryTab. Without
  // this, App Store review threads render an empty bubble.
  const bodyText = message.body || message.body_text || ''

  return (
    <div
      style={{
        ...styles.container,
        alignSelf: isOutbound ? 'flex-end' : 'flex-start',
        maxWidth: '80%',
      }}
    >
      {/* Sender / timestamp line */}
      <div
        style={{
          ...styles.metaRow,
          flexDirection: isOutbound ? 'row-reverse' : 'row',
        }}
      >
        <span style={styles.sender}>
          {isOutbound ? 'You' : message.from}
        </span>
        <span style={styles.timestamp}>
          {formatUnixSeconds(message.received_at, 'MMM d, h:mm a')}
        </span>
        {message.has_attachment && (
          <span
            style={styles.attachmentIcon}
            aria-label="Has attachment"
            title="Has attachment"
          >
            <Paperclip size={12} />
          </span>
        )}
      </div>

      {/* CC recipients — shown when present; not surfaced for BCC since
          the field is stripped from headers before delivery */}
      {message.cc && message.cc.length > 0 && (
        <div style={styles.ccRow}>
          <span style={styles.ccLabel}>CC:</span>
          <span style={styles.ccAddresses}>{message.cc.join(', ')}</span>
        </div>
      )}

      {/* Message body */}
      <div
        style={{
          ...styles.bubble,
          background: isOutbound
            ? 'color-mix(in srgb, var(--accent-primary) 16%, transparent)'
            : 'var(--bg-secondary)',
          borderColor: isOutbound
            ? 'color-mix(in srgb, var(--accent-primary) 30%, transparent)'
            : 'var(--border-subtle)',
          borderRadius: isOutbound
            ? 'var(--radius-lg) var(--radius-lg) var(--radius-sm) var(--radius-lg)'
            : 'var(--radius-lg) var(--radius-lg) var(--radius-lg) var(--radius-sm)',
          boxShadow: isOutbound
            ? '0 2px 8px color-mix(in srgb, #000 25%, transparent)'
            : '0 1px 3px color-mix(in srgb, #000 15%, transparent)',
        }}
      >
        <ChatMarkdown text={bodyText} density="compact" style={styles.body} />

        {/* Attachments list */}
        {message.attachments && message.attachments.length > 0 && (
          <div style={styles.attachmentsList}>
            {message.attachments.map((att) => (
              <div key={att.id} style={styles.attachment}>
                <Paperclip size={11} style={{ flexShrink: 0 }} />
                <span style={styles.attachmentName}>{att.filename}</span>
                <span style={styles.attachmentSize}>
                  ({(att.size / 1024).toFixed(0)} KB)
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Raw headers toggle (L4 drill-down) */}
      {message.raw_headers && (
        <button
          type="button"
          style={styles.headersToggle}
          onClick={() => setShowHeaders((v) => !v)}
          aria-expanded={showHeaders}
        >
          {showHeaders ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
          <span>{showHeaders ? 'Hide' : 'Show'} raw headers</span>
        </button>
      )}

      {showHeaders && message.raw_headers && (
        <div style={styles.headersContainer}>
          <JsonInspector
            data={message.raw_headers}
            density="compact"
            defaultExpand="first-level"
          />
        </div>
      )}
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  sender: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  timestamp: {
    fontSize: '11px',
    color: 'var(--text-muted)',
  },
  attachmentIcon: {
    color: 'var(--text-muted)',
    display: 'flex',
    alignItems: 'center',
  },
  ccRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '4px',
    fontSize: '11px',
    color: 'var(--text-muted)',
    marginTop: '1px',
  },
  ccLabel: {
    fontWeight: 600,
    flexShrink: 0,
  },
  ccAddresses: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  bubble: {
    padding: '10px 14px',
    border: '1px solid',
  },
  body: {
    fontSize: '13px',
    lineHeight: 1.6,
    color: 'var(--text-primary)',
    wordBreak: 'break-word',
  },
  attachmentsList: {
    marginTop: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  attachment: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: 'var(--text-secondary)',
  },
  attachmentName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  attachmentSize: {
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
  headersToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: 'var(--text-muted)',
    background: 'transparent',
    border: 'none',
    padding: '2px 0',
    cursor: 'pointer',
    transition: 'color var(--transition-fast)',
  },
  headersContainer: {
    marginTop: '4px',
    borderRadius: 'var(--radius-sm)',
    overflow: 'hidden',
    border: '1px solid var(--border-subtle)',
  },
}

export default MessageBubble
